'use memo';

import { useState } from 'react';
import {
  AgxButton,
  AgxGlyph,
  AgxSpacer,
  AgxTypography,
  Icon_Arrow,
  Icon_Reverse,
} from '@urbanx/agx.ui-components2';
import { CampaignStageAction } from 'Api/Campaigns/Types/campaignTypes';
import { usePerformCampaignAction } from 'features/campaigns/campaignsReducer';
import { Tabs } from 'types/Tabs';
import { useSelectedCampaign } from 'hooks/useSelectedCampaign';
import './FormDetailsHeader.scss';
import { CampaignDetailModelStage } from '@urbanx/agx.campaigns.client';

interface FormDetailsHeaderProps {
  id?: string;
  formName?: string;
  detail: string;
  currentTab?: Tabs;
  onBack?: () => void;
}

const FormDetailsHeader = ({
  id,
  formName,
  detail,
  currentTab,
  onBack,
}: FormDetailsHeaderProps) => {
  const { selectedCampaign, isSelectedCampaignPending } = useSelectedCampaign();

  const performCampaignAction = usePerformCampaignAction();

  const [disableArchiveButton, setDisableArchiveButton] = useState(false);

  const { campaignId, stage: campaignStage } = selectedCampaign ?? {};

  const performAction = async (action: CampaignStageAction) => {
    if (!campaignId) return;

    await performCampaignAction({
      campaignId,
      campaignAction: action,
    });

    setDisableArchiveButton(false);
  };
  
  const onArchiveCampaign = () => {
    if (disableArchiveButton) return;
    setDisableArchiveButton(true);
    performAction(CampaignStageAction.Archive);
  };

  const onRestoreCampaign = () => {
    performAction(CampaignStageAction.Restore);
  };

  if (isSelectedCampaignPending) return null;

  const isCampaignStageValid =
    campaignStage === CampaignDetailModelStage.AgreementDraft;

  const shouldRenderRestore =
    isCampaignStageValid && currentTab === Tabs.Archive;
  const shouldRenderArchive =
    isCampaignStageValid &&
    currentTab !== Tabs.Archive &&
    currentTab !== Tabs.Listings;

  const title = (
    <AgxTypography
      id={`agx-${id}-form-name`}
      type="caption"
      size="medium"
      dataTestId={`agx-${id}-form-name`}
      color="var(--text-tertiary)"
    >
      {formName}
    </AgxTypography>
  );

  const backButton = !!onBack && (
    <button
      type="button"
      className="formDetailsBack"
      onClick={onBack}
      aria-label="Back"
    >
      <Icon_Arrow
        direction="left"
        data-testid={`agx-${id}-arrow-back`}
        height={18}
        width={18}
        color="var(--text-tertiary)"
      />
    </button>
  );

  const detailSection = (
    <AgxTypography
      id={`agx-${id}-date`}
      type="caption"
      size="medium"
      color="var(--text-tertiary)"
      dataTestId={`agx-${id}-date`}
    >
      {detail}
    </AgxTypography>
  );

  return (
    <>
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        contentAlignment="center"
        fill
        className="formDetailsHeaderMobile"
      >
        <div>
          {shouldRenderRestore && (
            <button
              type="button"
              className="restore"
              onClick={onRestoreCampaign}
              aria-label="Restore"
            >
              <Icon_Reverse
                height={18}
                width={18}
                color="var(--text-tertiary)"
              />
            </button>
          )}
          {shouldRenderArchive && !disableArchiveButton && (
            <button
              type="button"
              className="archive"
              onClick={onArchiveCampaign}
              aria-label="Archive"
            >
              <AgxGlyph type="box" height={18} width={18} />
            </button>
          )}
        </div>
        {backButton}
        <AgxSpacer
          contentJustification="center"
          contentAlignment="center"
          className="formDetailsTitleLastUpdated"
        >
          {title}
          {detailSection}
        </AgxSpacer>
      </AgxSpacer>
      <AgxSpacer
        direction="horizontal"
        contentJustification="space-between"
        contentAlignment="center"
        fill
        className="formDetailsHeaderDesktop"
      >
        <AgxSpacer
          direction="horizontal"
          gap="spacing-xs"
          contentAlignment="center"
          className="formDetailsTitleInfo"
          onClick={onBack}
        >
          {backButton}
          {title}
        </AgxSpacer>
        {detailSection}
        {shouldRenderRestore && (
          <AgxButton
            id={`agx-${id}-restore`}
            variant="naked"
            size="small"
            text="Restore"
            minimal
            onClick={onRestoreCampaign}
          />
        )}
        {shouldRenderArchive && (
          <AgxButton
            id={`agx-${id}-archive`}
            variant="naked"
            size="small"
            text="Archive"
            disabled={disableArchiveButton}
            minimal
            onClick={onArchiveCampaign}
          />
        )}
      </AgxSpacer>
    </>
  );
};

export default FormDetailsHeader;
